<!--
  Shipment tracking widget — rewritten 2026-06-09.

  Previously a `<kendo-dialog>` + `<k-overlay>` + a red close button +
  Ionic/FontAwesome icons inside cards. Now renders inline inside the
  parent modal (agente-list or client-list owns the modal chrome) and
  uses emoji-based status icons.

  Three sections:
    1. Header   — N° ordine, corriere, status badge, big status banner
    2. CTA link — opens the carrier's external tracking URL
    3. Timeline — vertical list of shipment steps with emoji icons
-->

@if (loading) {

  <!-- ─── Loading skeleton ───────────────────────────────────────── -->
  <div class="tracking-skeleton" aria-busy="true">
    <div class="tracking-skeleton__banner"></div>
    <div class="tracking-skeleton__row tracking-skeleton__row--lg"></div>
    @for (i of [1, 2, 3, 4]; track i) {
      <div class="tracking-skeleton__step">
        <div class="tracking-skeleton__icon"></div>
        <div class="tracking-skeleton__row tracking-skeleton__row--md"></div>
      </div>
    }
  </div>

} @else if (!shipment) {

  <!-- ─── Empty state ────────────────────────────────────────────── -->
  <div class="tracking-empty">
    <div class="tracking-empty__icon" aria-hidden="true">📭</div>
    <p class="tracking-empty__msg">
      Nessuna informazione di tracking disponibile per questa spedizione.
    </p>
  </div>

} @else {

  <article class="tracking">

    <!-- ─── Status banner ──────────────────────────────────────── -->
    <header class="tracking-banner" [attr.data-variant]="bannerVariant">
      <span class="tracking-banner__emoji" aria-hidden="true">
        {{ iconForStep(shipment.sm_status_cod) }}
      </span>
      <div class="tracking-banner__text">
        <span class="tracking-banner__title">{{ shipment.sm_status_desc || '—' }}</span>
        <span class="tracking-banner__meta">
          @if (shipment.dateS) { Spedito il <strong>{{ shipment.dateS }}</strong> }
          @if (shipment.dateS && shipment.sm_carrier_desc) { · }
          @if (shipment.sm_carrier_desc) { Corriere <strong>{{ shipment.sm_carrier_desc }}</strong> }
        </span>
      </div>
    </header>

    <!-- ─── Header info ────────────────────────────────────────── -->
    <section class="tracking-info" aria-label="Dati spedizione">
      <dl class="tracking-info__grid">
        <div class="tracking-info__row">
          <dt>N° Ordine</dt>
          <dd class="tracking-info__val--accent">{{ shipment.or_order_number || '—' }}</dd>
        </div>
        <div class="tracking-info__row">
          <dt>Numero tracking</dt>
          <dd>{{ shipment.sm_tracking_number || '—' }}</dd>
        </div>
        <div class="tracking-info__row">
          <dt>Stato</dt>
          <dd>
            <span class="status-pill" [attr.data-variant]="statusVariant">
              {{ shipment.sm_status_desc || '—' }}
            </span>
          </dd>
        </div>
        @if (shipment.or_shipping_address) {
          <div class="tracking-info__row tracking-info__row--full">
            <dt>Indirizzo di consegna</dt>
            <dd>{{ shipment.or_shipping_address }}</dd>
          </div>
        }
      </dl>
    </section>

    <!-- ─── External tracking link ─────────────────────────────── -->
    @if (shipment.sm_ext_link) {
      <button
        type="button"
        class="tracking-link"
        (click)="openExternalLink(shipment.sm_ext_link)"
        [title]="shipment.sm_ext_link"
      >
        <span class="tracking-link__icon" aria-hidden="true">🔗</span>
        <span class="tracking-link__text">
          <span class="tracking-link__label">Apri tracking sul sito del corriere</span>
          <span class="tracking-link__url">{{ shipment.sm_ext_link }}</span>
        </span>
        <span class="tracking-link__chevron" aria-hidden="true">›</span>
      </button>
    }

    <!-- ─── Timeline ───────────────────────────────────────────── -->
    <section class="tracking-timeline" aria-label="Storico spedizione">
      <header class="tracking-timeline__header">
        <h3 class="tracking-timeline__title">📍 Storico</h3>
        @if (hasSteps) {
          <span class="tracking-timeline__count">
            {{ shipment.DetailsIcons.length }}
            {{ shipment.DetailsIcons.length === 1 ? 'aggiornamento' : 'aggiornamenti' }}
          </span>
        }
      </header>

      @if (!hasSteps) {
        <p class="tracking-timeline__empty">
          Nessun aggiornamento di stato ancora ricevuto dal corriere.
        </p>
      } @else {
        <ol class="tracking-steps" role="list">
          @for (step of shipment.DetailsIcons; track $index; let last = $last) {
            <li class="step" [attr.data-status]="step.StatusCode" [class.step--last]="last">
              <span class="step__icon" aria-hidden="true">{{ iconForStep(step.StatusCode) }}</span>
              <div class="step__body">
                <span class="step__msg">{{ step.message || '—' }}</span>
                @if (step.city) {
                  <span class="step__city">📍 {{ step.city }}</span>
                }
                @if (step.date) {
                  <span class="step__date">{{ step.date }}</span>
                }
              </div>
            </li>
          }
        </ol>
      }
    </section>

  </article>
}
